import catalogJson from "../../../../config/questionnaire-rule-catalog.json";
import assetFamilies from "../../../../config/questionnaire-asset-families.json";
import capabilities from "../../../../config/questionnaire-capabilities.json";
import conditionOperators from "../../../../config/questionnaire-condition-operators.json";
import questionCapabilities from "../../../../config/questionnaire-question-capabilities.json";
import ratingScales from "../../../../config/questionnaire-rating-scales.json";
import recipeRegistry from "../../../../config/questionnaire-recipe-registry.json";
import reportCardCapabilities from "../../../../config/questionnaire-report-card-capabilities.json";
import scaleWording from "../../../../config/questionnaire-scale-wording.json";
import textLimits from "../../../../config/questionnaire-text-limits.json";
import vocabularies from "../../../../config/questionnaire-vocabularies.json";
import { englishList, englishNumber } from "./rating-scales";
import type { RuleCode, RuleEntry, RuleSeverityByWriter } from "./rule-codes.generated";

/**
 * Every rule the questionnaire builder enforces, declared once
 * (`config/questionnaire-rule-catalog.json`, doc 02 §1), shared with Python
 * (`apps/python-api/models/questionnaire/rule_catalog.py`, which loads and
 * checks it, and renders the model-facing sentences). `RuleCode` is generated
 * from the Python owner, so a `Record<RuleCode, …>` map is exhaustive.
 *
 * Templates use `{name}` placeholders, filled from the entry's `facts`
 * (`<registry>#<key path>` into `config/questionnaire-*.json`) and its declared
 * `params`; a list joins as English (`{name:and}`, default `or`); `{{` and
 * `}}` are literal braces. Python's loader refuses an unresolvable fact, a
 * placeholder with no source and a literal fact number, so this reader only
 * fills them.
 */
export type {
  RuleChecker,
  RuleCode,
  RuleEntry,
  RuleSeverityByWriter,
} from "./rule-codes.generated";

export type RuleWriter = keyof RuleSeverityByWriter;
export type RuleSeverity = NonNullable<RuleSeverityByWriter["model"]>;

if (catalogJson.schemaVersion !== "questionnaire-rule-catalog.v1") {
  throw new Error("Unsupported questionnaire rule catalog schema");
}

const REGISTRIES: Readonly<Record<string, unknown>> = Object.freeze({
  "questionnaire-asset-families": assetFamilies,
  "questionnaire-capabilities": capabilities,
  "questionnaire-condition-operators": conditionOperators,
  "questionnaire-question-capabilities": questionCapabilities,
  "questionnaire-rating-scales": ratingScales,
  "questionnaire-recipe-registry": recipeRegistry,
  "questionnaire-report-card-capabilities": reportCardCapabilities,
  "questionnaire-scale-wording": scaleWording,
  "questionnaire-text-limits": textLimits,
  "questionnaire-vocabularies": vocabularies,
});

const ENTRIES: ReadonlyMap<RuleCode, RuleEntry> = new Map(
  (catalogJson.rules as unknown as RuleEntry[]).map((entry) => [
    entry.code,
    Object.freeze(entry),
  ]),
);

/** Every code, in catalog order. */
export const RULE_CODES: ReadonlyArray<RuleCode> = Object.freeze([
  ...ENTRIES.keys(),
]);

export function isRuleCode(code: string): code is RuleCode {
  return ENTRIES.has(code as RuleCode);
}

/**
 * Whether `code` is a catalog turn entry (`kind: "turn"`): a code a failed
 * turn may carry, with its user line and its retry verdict (`turnRetryable`).
 */
export function isTurnCode(code: string): code is RuleCode {
  return isRuleCode(code) && ENTRIES.get(code)?.kind === "turn";
}

/** The catalog entry for `code`; throws for a code with none. */
export function ruleEntry(code: RuleCode): RuleEntry {
  const entry = ENTRIES.get(code);
  if (!entry) throw new Error(`no rule catalog entry for ${code}`);
  return entry;
}

/**
 * What `writer` gets when `code` fails: the entry's one decision for it.
 * Throws when that writer kind never writes the field, so no layer invents
 * a severity of its own.
 */
export function policyFor(code: RuleCode, writer: RuleWriter): RuleSeverity {
  const decision = ruleEntry(code).severity?.[writer];
  if (!decision) throw new Error(`${code}: no ${writer} severity`);
  return decision;
}

/**
 * `writer`'s decision on `code`, or undefined when the catalog states none: a
 * code it does not hold, or a turn or failure entry, which no write breaks.
 */
export function policyIfAny(code: string, writer: RuleWriter): RuleSeverity | undefined {
  return (isRuleCode(code) ? ruleEntry(code).severity?.[writer] : undefined) ?? undefined;
}

/**
 * Whether the write gate refuses a write by `writer` that leaves a new issue
 * of `code`: the writer's own catalog decision is `refuse`, `reject` or
 * `invariant`. A `block` is saved and stops Review; a `warn` is shown. A
 * writer the entry gives no decision never meets the rule, so it never
 * refuses that writer's write.
 */
export function refusesWrite(code: string, writer: RuleWriter): boolean {
  const decision = policyIfAny(code, writer);
  return decision === "refuse" || decision === "reject" || decision === "invariant";
}

/**
 * The user's decision on an issue of `code`: the one severity every reader of
 * a draft's issues reads (readiness, compile, validate, the editor, the
 * placer). Throws for a code with no entry or no user decision, so no reader
 * invents one.
 */
export function userDecisionOf(code: string): RuleSeverity {
  if (!isRuleCode(code)) throw new Error(`${code}: no rule catalog entry`);
  return policyFor(code, "user");
}

/** Whether an issue of `code` stops Review and launch: anything but `warn`. */
export function blocksReview(code: string): boolean {
  return userDecisionOf(code) !== "warn";
}

/**
 * The display severity of an issue of `code`, read from its user decision:
 * `warning` for `warn`, `error` for anything that stops Review. Every raise
 * site states it this way, so no layer chooses a severity of its own.
 */
export function issueSeverity(code: string): "error" | "warning" {
  return blocksReview(code) ? "error" : "warning";
}

/**
 * Whether an issue of `code` stops Preview (compile): only what no write may
 * store, `refuse`, `reject` and `invariant`. A `block` stops Review, never
 * Preview, so an unfinished study still previews (owner, 2026-10-22).
 */
export function stopsPreview(code: string): boolean {
  const decision = userDecisionOf(code);
  return decision === "refuse" || decision === "reject" || decision === "invariant";
}

/**
 * Whether a user can act on `code`: its entry gives the user writer a
 * decision that is theirs to meet (`refuse`, `reject` or `block`). A code the
 * user writer never meets, or meets only as our bug (`invariant`), is not.
 */
export function userCanAct(code: RuleCode): boolean {
  const decision = ruleEntry(code).severity?.user;
  return decision === "refuse" || decision === "reject" || decision === "block";
}

/**
 * Who breaks `code` when a chat turn still refuses it after its repair
 * (C07-3): the user's request (`request`, the turn tells them the entry's
 * line) or the model's own batch (`batch`, the turn fails typed and
 * retryable). Chat turns are model-written, so the user writer's severity
 * cannot say it. Throws for an entry that declares none (only a model
 * `refuse` or `block` does).
 */
export function turnCauseOf(code: RuleCode): "request" | "batch" {
  const cause = ruleEntry(code).turnCause;
  if (cause !== "request" && cause !== "batch") {
    throw new Error(`${code}: the rule catalog records no turn cause`);
  }
  return cause;
}

/**
 * Whether only the user's request can break `code`: its turn cause is the
 * request and its entry records no split (`splitNeeded`: the model's own
 * batch can break it too). A refusal of such a rule is no slip a repair
 * could fix.
 */
export function brokenOnlyByRequest(code: RuleCode): boolean {
  const entry = ruleEntry(code);
  return entry.turnCause === "request" && !entry.splitNeeded;
}

/**
 * Whether the user may retry a turn that failed on `code`: its turn entry's
 * one verdict, read by every route and by the builder. Throws for an entry
 * that records none.
 */
export function turnRetryable(code: RuleCode): boolean {
  const retryable = ruleEntry(code).retryable;
  if (typeof retryable !== "boolean") {
    throw new Error(`${code}: the rule catalog records no retry verdict`);
  }
  return retryable;
}

/** Who breaks a rule a chat turn still refuses after its repair (`turnCause`). */
export type RuleTurnCause = NonNullable<RuleEntry["turnCause"]>;

/**
 * Whether a turn that stopped on refusals may be retried, from who broke
 * them (`turnCause`): only the model's own batch (`batch`); the user's
 * request (`request`) meets the same refusal on the next turn.
 */
export function turnCauseRetryable(cause: RuleTurnCause): boolean {
  return cause === "batch";
}

/** The value a `<registry>#<dotted key path>` reference names. */
export function ruleFact(ref: string): unknown {
  const [registry, keyPath] = ref.split("#", 2);
  let value: unknown = REGISTRIES[registry];
  if (value === undefined || !keyPath) {
    throw new Error(`fact reference ${ref} names no registry key`);
  }
  for (const key of keyPath.split(".")) {
    if (typeof value !== "object" || value === null || !(key in value)) {
      throw new Error(`fact reference ${ref} does not resolve at ${key}`);
    }
    value = (value as Record<string, unknown>)[key];
  }
  return value;
}

export type RuleParams = Readonly<Record<string, string | number | ReadonlyArray<string>>>;

const PLACEHOLDER = /\{\{|\}\}|\{([A-Za-z_][\w.]*)(?::(and|or|word))?\}/g;

/**
 * The values `code`'s templates are filled from: its facts, then `params`.
 * A translation of its user line keyed by code reads the same values.
 */
export function ruleTemplateValues(
  code: RuleCode,
  params: RuleParams = {},
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const [name, ref] of Object.entries(ruleEntry(code).facts ?? {})) {
    // A fact keyed by a param one line names and another does not is read
    // only when that param is given, as Python's `_facts` does.
    if (keyParams(ref).some((param) => typeof params[param] !== "string")) continue;
    values[name] = ruleFact(keyedFactRef(code, ref, params));
  }
  return Object.assign(values, params);
}

function keyParams(ref: string): Array<string> {
  return [...ref.matchAll(/\{([A-Za-z_]\w*)\}/g)].map((match) => match[1]);
}

/**
 * A fact reference keyed by a typed param (`...#families.{family}.nouns.limit`)
 * with the param's value in its place. Python's loader checks the reference
 * resolves for every key of that registry level.
 */
function keyedFactRef(code: RuleCode, ref: string, params: RuleParams): string {
  return ref.replace(/\{([A-Za-z_]\w*)\}/g, (_match, name: string) => {
    const value = params[name];
    if (typeof value !== "string") {
      throw new Error(`${code}: fact ${ref} needs the param {${name}}`);
    }
    return value;
  });
}

function render(entry: RuleEntry, template: string, params: RuleParams = {}): string {
  return fillRuleTemplate(entry.code, template, ruleTemplateValues(entry.code, params));
}

/**
 * A catalog line with its `{name}`, `{name:and|or}` and `{name:word}` slots
 * filled from `values`; it throws on a slot it cannot fill. Python's
 * `rule_catalog._render` is the other reader of the same templates, held to
 * these answers by the generated cross-language table (`ruleLine`).
 */
export function fillRuleTemplate(
  code: string,
  template: string,
  values: Readonly<Record<string, unknown>>,
): string {
  const entry = { code };
  return template.replace(
    PLACEHOLDER,
    (match, name: string | undefined, conjunction: "and" | "or" | "word" | undefined) => {
      if (!name) return match[0];
      if (!(name in values)) {
        throw new Error(`${entry.code}: no value for {${name}}`);
      }
      const value = values[name];
      if (conjunction === "word") {
        if (typeof value !== "number" || !Number.isInteger(value)) {
          throw new Error(`${entry.code}: {${name}:word} is not a whole number`);
        }
        return englishNumber(value);
      }
      if (Array.isArray(value)) {
        return englishList(value.map(String), conjunction ?? "or");
      }
      if (typeof value === "string" || typeof value === "number") return String(value);
      throw new Error(`${entry.code}: {${name}} is not text, a number or a list`);
    },
  );
}

/** The user's line for `code`, facts and `params` filled; undefined when it has none. */
export function userText(code: RuleCode, params?: RuleParams): string | undefined {
  const entry = ruleEntry(code);
  return entry.user == null ? undefined : render(entry, entry.user, params);
}

/**
 * The repair message for `code`, facts and `params` filled (`path` is a
 * param); undefined when it has none.
 */
export function repairText(code: RuleCode, params?: RuleParams): string | undefined {
  const entry = ruleEntry(code);
  return entry.repair == null ? undefined : render(entry, entry.repair, params);
}

/**
 * The words a check refuses `code` with: its catalog repair line, facts and
 * `params` filled. A model told the refusal reads exactly this line; throws
 * for an entry with none (the catalog guard fails such a site first).
 */
export function repairLine(code: RuleCode, params?: RuleParams): string {
  const line = repairText(code, params);
  if (line === undefined) throw new Error(`${code}: no repair line`);
  return line;
}

/**
 * A refusal of `code` told in its check's own `message` rather than its
 * catalog repair line: the check words the rule more than one way, and moving
 * those words would change what a model reads. The rule-catalog guard lists
 * every such code (`TS_WORDED_RULES`, scripts/rule_catalog/guards.py).
 */
export function worded(code: RuleCode, message: string): string {
  ruleEntry(code);
  return message;
}

/**
 * The params `code`'s `line` is filled from: those it names, and those a fact
 * it names is keyed by (`...#families.{family}.nouns.limit`).
 */
export function lineParams(code: RuleCode, line: "user" | "repair"): ReadonlyArray<string> {
  const entry = ruleEntry(code);
  const template = entry[line];
  if (template == null) return [];
  const declared = new Set(entry.params ?? []);
  const facts: Record<string, string> = entry.facts ?? {};
  const named = [...template.matchAll(PLACEHOLDER)]
    .map((match) => match[1])
    .filter((name): name is string => name !== undefined)
    .flatMap((name) => (name in facts ? keyParams(facts[name]) : [name]))
    .filter((name) => declared.has(name));
  return [...new Set(named)];
}
